import client from 'client'
import type {
  ResendAssetPositionFileAssetPositionAssetPositionIdFilesFileIdPostMutationRequest,
  ResendAssetPositionFileAssetPositionAssetPositionIdFilesFileIdPostMutationResponse,
  ResendAssetPositionFileAssetPositionAssetPositionIdFilesFileIdPostPathParams,
  ResendAssetPositionFileAssetPositionAssetPositionIdFilesFileIdPostQueryParams,
  ResendAssetPositionFileAssetPositionAssetPositionIdFilesFileIdPost422,
} from '../../types/ResendAssetPositionFileAssetPositionAssetPositionIdFilesFileIdPost.ts'
import type { UseMutationOptions } from '@tanstack/react-query'
import type { RequestConfig, ResponseConfig, ResponseErrorConfig } from 'client'
import { useMutation } from '@tanstack/react-query'

export const resendAssetPositionFileAssetPositionAssetPositionIdFilesFileIdPostMutationKey = () =>
  [{ url: '/asset_position/{asset_position_id}/files/{file_id}' }] as const

export type ResendAssetPositionFileAssetPositionAssetPositionIdFilesFileIdPostMutationKey = ReturnType<
  typeof resendAssetPositionFileAssetPositionAssetPositionIdFilesFileIdPostMutationKey
>

/**
 * @summary Resend Asset Position File
 * {@link /asset_position/:asset_position_id/files/:file_id}
 */
export async function resendAssetPositionFileAssetPositionAssetPositionIdFilesFileIdPost(
  asset_position_id: ResendAssetPositionFileAssetPositionAssetPositionIdFilesFileIdPostPathParams['asset_position_id'],
  file_id: ResendAssetPositionFileAssetPositionAssetPositionIdFilesFileIdPostPathParams['file_id'],
  data: ResendAssetPositionFileAssetPositionAssetPositionIdFilesFileIdPostMutationRequest,
  params?: ResendAssetPositionFileAssetPositionAssetPositionIdFilesFileIdPostQueryParams,
  config: Partial<RequestConfig<ResendAssetPositionFileAssetPositionAssetPositionIdFilesFileIdPostMutationRequest>> & { client?: typeof client } = {},
) {
  const { client: request = client, ...requestConfig } = config

  const res = await request<
    ResendAssetPositionFileAssetPositionAssetPositionIdFilesFileIdPostMutationResponse,
    ResponseErrorConfig<ResendAssetPositionFileAssetPositionAssetPositionIdFilesFileIdPost422>,
    ResendAssetPositionFileAssetPositionAssetPositionIdFilesFileIdPostMutationRequest
  >({ method: 'POST', url: `/asset_position/${asset_position_id}/files/${file_id}`, params, data, ...requestConfig })
  return res
}

/**
 * @summary Resend Asset Position File
 * {@link /asset_position/:asset_position_id/files/:file_id}
 */
export function useResendAssetPositionFileAssetPositionAssetPositionIdFilesFileIdPost(
  options: {
    mutation?: UseMutationOptions<
      ResponseConfig<ResendAssetPositionFileAssetPositionAssetPositionIdFilesFileIdPostMutationResponse>,
      ResponseErrorConfig<ResendAssetPositionFileAssetPositionAssetPositionIdFilesFileIdPost422>,
      {
        asset_position_id: ResendAssetPositionFileAssetPositionAssetPositionIdFilesFileIdPostPathParams['asset_position_id']
        file_id: ResendAssetPositionFileAssetPositionAssetPositionIdFilesFileIdPostPathParams['file_id']
        data: ResendAssetPositionFileAssetPositionAssetPositionIdFilesFileIdPostMutationRequest
        params?: ResendAssetPositionFileAssetPositionAssetPositionIdFilesFileIdPostQueryParams
      }
    >
    client?: Partial<RequestConfig<ResendAssetPositionFileAssetPositionAssetPositionIdFilesFileIdPostMutationRequest>> & { client?: typeof client }
  } = {},
) {
  const { mutation: mutationOptions, client: config = {} } = options ?? {}
  const mutationKey = mutationOptions?.mutationKey ?? resendAssetPositionFileAssetPositionAssetPositionIdFilesFileIdPostMutationKey()

  return useMutation<
    ResponseConfig<ResendAssetPositionFileAssetPositionAssetPositionIdFilesFileIdPostMutationResponse>,
    ResponseErrorConfig<ResendAssetPositionFileAssetPositionAssetPositionIdFilesFileIdPost422>,
    {
      asset_position_id: ResendAssetPositionFileAssetPositionAssetPositionIdFilesFileIdPostPathParams['asset_position_id']
      file_id: ResendAssetPositionFileAssetPositionAssetPositionIdFilesFileIdPostPathParams['file_id']
      data: ResendAssetPositionFileAssetPositionAssetPositionIdFilesFileIdPostMutationRequest
      params?: ResendAssetPositionFileAssetPositionAssetPositionIdFilesFileIdPostQueryParams
    }
  >({
    mutationFn: async ({ asset_position_id, file_id, data, params }) => {
      return resendAssetPositionFileAssetPositionAssetPositionIdFilesFileIdPost(asset_position_id, file_id, data, params, config)
    },
    mutationKey,
    ...mutationOptions,
  })
}